import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useState } from "react";
import inputStyles from "../../../../../assets/styles/components/input.module.css";
import Button from "../../../../../components/Button/Button";
import styles from "./CategoriesProducts.module.css";

import {
  cancelTableCategoryProduct,
  getCategoriesProducts,
  type IordemCategoryProduct,
} from "../../../../../services/products";

import type { IResultCategoriesProducts } from "../../../../../types/products";
import TableContainer from "../../../../../components/TableContainer/TableContainer";

import { Add, Search, ArrowBack, ArrowDropDown, ArrowDropUp, Delete, Edit } from "@mui/icons-material";

import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Modal from "./Modal";
import ModalGeneric from "../../../../../components/ModalGeneric/ModalGeneric";
import InputSearch from "../../../../../components/InputSearch/InputSearch";

type fieldOrder = 'title';

const CategoriesProducts = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [isHidden, setIsHidden] = useState<{
    isVisible?: boolean;
    uid?: string;
    value?: string;
  }>({
    isVisible: false,
    uid: "",
    value: "",
  });
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);
  
  const [tempSearch, setTempSearch] = useState("");
  const [search, setSearch] = useState("");

  const [orderTable, setOrderTable] = useState<IordemCategoryProduct>({ title: "ASC" });

  const { data, fetchNextPage, hasNextPage, isLoading } =
    useInfiniteQuery<IResultCategoriesProducts>({
      queryKey: ["getCategoriesProducts", search, orderTable],
      queryFn: ({ pageParam = 1 }) =>
        getCategoriesProducts(search, pageParam as number, orderTable),
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.data.hasMore) {
          return allPages.length + 1;
        }
        return undefined;
      },
      initialPageParam: 1,
    });

  const { mutate } = useMutation({
    mutationFn: async (uid: string) => {
      const res = await cancelTableCategoryProduct(uid);
      return res;
    },
    onSuccess: (response) => {
      toast.success(response?.message);
      queryClient.invalidateQueries({
        queryKey: ["getCategoriesProducts"],
      });
    },
    onError: (err: any) => {
      toast.error(err.message);
    },
  });

  const handleOpenModalAdd = () => {
    setIsHidden((prev) => ({ ...prev, isVisible: true }));
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTempSearch(event.target.value);
  };

  const handleDelete = (uid_fornecedor: string) => {
    setItemToDelete(uid_fornecedor);
    setIsConfirmOpen(true);
  };

  const handleChangeOrder = (field: fieldOrder) => {
    setOrderTable({
      [field]: orderTable[field] === "ASC" ? "DESC" : "ASC",
    });
  };

  const renderSortableHeader = (field: fieldOrder, label: string) => {
    return (
      <Button
        variant="textButton"
        onClick={() => handleChangeOrder(field)}
        title={orderTable[field] === "DESC" ? "Decrescente" : "Crescente"}
      >
        {label}
        {orderTable[field]
          ? orderTable[field] === "DESC"
            ? <ArrowDropDown />
            : <ArrowDropUp />
          : ""
        }
      </Button>
    );
  };

  // evita renderização enquanto digita a pesquisa, executa a pesquisa e renderiza após 0,5s
  useEffect(() => {
    const handler = setTimeout(() => setSearch(tempSearch), 500);
    return () => clearTimeout(handler);
  }, [tempSearch]);

  return (
    <div className={styles.containerPage}>
      <div className={styles.contentWrapper}>
        <div className={styles.header}>
          <h1>Categorias de Produtos</h1>

          <InputSearch
            searchValue={tempSearch}
            setSearchValue={setTempSearch}
            placeholder="Pesquisar por título ou descrição"
          />

          <div className={styles.headerButtons}>
            <p>
              Qtd:{" "}
              <span>
                {data?.pages?.length
                  ? data.pages[data.pages.length - 1]?.data?.qtd ?? 0
                  : 0}
              </span>
            </p>

            <Button
              variant="contain"
              onClick={handleOpenModalAdd}
              title="Adicionar novo tipo de custo">
              <Add />
            </Button>
            <Button
              variant="contain"
              onClick={() => navigate(-1)}
              title="Voltar para página anterior">
              <ArrowBack />
            </Button>
          </div>
        </div>

        <div className={styles.containerTable}>
          <TableContainer
            columns={[
              { 
                key: "tituloEx", 
                titulo: renderSortableHeader("title", "Título"),
                classStyle: styles.columnTitle 
              },
              { key: "actionEx", titulo: "Ação", classStyle: styles.columnAction },
            ]}
            content={
              data?.pages?.flatMap((page) =>
                page.data.categories.map((c: any) => ({
                  values: {
                    tituloEx: c.titulo,
                  },
                  buttons: [
                    {
                      variant: "contain",
                      typeStyle: "small",
                      title: "Editar categoria",
                      onClick: () =>
                        setIsHidden({
                          isVisible: true,
                          uid: c.uid_produto_categoria,
                          value: c.titulo,
                        }),
                      icon: <Edit />,
                    },
                    {
                      variant: "contain",
                      typeStyle: ["small", "cancel"], // Regra 3: Botão de exclusão
                      title: "Deletar categoria",
                      onClick: () => handleDelete(c.uid_produto_categoria),
                      icon: <Delete />,
                    },
                  ],
                }))
              ) ?? []
            }
          />
        </div>

        <div className={styles.loadMore}>
          {hasNextPage ? (
            <Button variant="contain" onClick={() => fetchNextPage()}>
              {isLoading ? "Carregando..." : "Carregar Mais registros"}
            </Button>
          ) : (
            <p className={styles.tableLastItem}>
              Todos os registros foram carregados
            </p>
          )}
        </div>
      </div>

      {isHidden.isVisible && (
        <Modal
          onClick={() => setIsHidden({ isVisible: false, uid: "" })}
          uid={isHidden.uid}
          title={isHidden.value}
        />
      )}
      {isConfirmOpen && (
        <ModalGeneric
          title={`Excluir Fornecedor`}
          onClick={() => setIsConfirmOpen(false)}
        >
          <p>Tem certeza que deseja excluir este fornecedor?</p>
          <div className={styles.actions}>
            <Button variant="contain" onClick={() => {
              setIsConfirmOpen(false);
            }}>Cancelar</Button>
            <Button variant="contain" onClick={() => {
              if (itemToDelete){
                mutate(itemToDelete);
              }
              setIsConfirmOpen(false);
            }}>Confirmar</Button>
          </div>
        </ModalGeneric>
      )}
    </div>
  );
};

export default CategoriesProducts;
